<script lang="ts">
/**
 * y 軸上界：資料最大值往上取到一個「整齊」的刻度（1／2／5 × 10ⁿ）。
 *
 * ★ 放在**普通 `<script>`**（不是 `<script setup>`）：後者不允許具名匯出，而這支純函式
 *   要給 `DailyCountChart.spec.ts` 直接測——「圖看起來對不對」在單元測試裡斷言不了，
 *   但「49 要變成 50、51 要變成 100、0 要變成 1（不能除以零）」可以。
 *   兩個 block 併用時，這裡的 binding 在 `<script setup>` 與 template 裡都用得到。
 */
export function niceMax(raw: number): number {
  if (!Number.isFinite(raw) || raw <= 0) return 1
  const exp = Math.floor(Math.log10(raw))
  const base = 10 ** exp
  for (const step of [1, 2, 5, 10]) {
    if (raw <= step * base) return step * base
  }
  return 10 * base
}
</script>

<script setup lang="ts">
/**
 * 每日計數折線圖（兩條線：發碼／兌換）——**純 inline SVG，不引圖表庫**（批107 項5）。
 *
 * ★★ 為什麼**不重用 `components/MetricsChart.vue`**：那支的 y 軸寫死 0–100%
 *   （`yOf()` 把值 clamp 到 0–100、刻度固定 `[0, 50, 100]`），因為它畫的是 CPU／RAM／磁碟
 *   使用率。計數沒有上界——一天發 800 張碼會被壓成一條貼齊上緣的直線，而 5 張與 50 張
 *   看起來一模一樣。量寬用的 ResizeObserver 做法沿用它（見那支檔頭的兩個實作決定）。
 *
 * 三個刻意的行為：
 *
 * 1. **零值照畫**：後端已經把區間內每一天補成 0，這裡就一路連過去。把零日跳過會讓
 *    「三天有資料」連成一條假趨勢線。
 * 2. **y 軸上界取資料最大值**（至少 1，再往上取整到好看的刻度）——全零時仍畫出
 *    一條貼底的線與 0／1 兩個刻度，而不是除以零。
 * 3. **x 軸最多 6 個標籤**：31 天的日期全印會糊成一片。頭尾的 `text-anchor` 改成
 *    start／end，否則第一個與最後一個標籤會有一半被容器切掉。
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import type { DailyPoint } from '@/api/redeemReports'

const props = withDefaults(
  defineProps<{
    points: DailyPoint[]
    height?: number
  }>(),
  { height: 220 },
)

type SeriesKey = 'issued' | 'redeemed'
const SERIES: { key: SeriesKey; label: string; cls: string }[] = [
  { key: 'issued', label: '發碼', cls: 's-issued' },
  { key: 'redeemed', label: '兌換', cls: 's-redeemed' },
]

const PAD = { l: 38, r: 12, t: 12, b: 22 }

// --- 量寬（ResizeObserver；理由同 MetricsChart）---
const box = ref<HTMLElement | null>(null)
const width = ref(720)
let ro: ResizeObserver | null = null

onMounted(() => {
  const el = box.value
  if (!el) return
  width.value = Math.max(240, el.clientWidth || 240)
  // jsdom 沒有 ResizeObserver（單元測試環境）——沒有就只用初始寬度，不要整個元件炸掉。
  if (typeof ResizeObserver === 'undefined') return
  ro = new ResizeObserver((entries) => {
    for (const e of entries) width.value = Math.max(240, Math.round(e.contentRect.width))
  })
  ro.observe(el)
})
onBeforeUnmount(() => {
  ro?.disconnect()
  ro = null
})

const innerW = computed(() => Math.max(1, width.value - PAD.l - PAD.r))
const innerH = computed(() => Math.max(1, props.height - PAD.t - PAD.b))

/** y 軸上界（`niceMax` 定義在上面那個普通 `<script>` block，見那裡的說明）。 */
const maxValue = computed(() =>
  niceMax(
    props.points.reduce((m, p) => Math.max(m, p.issued, p.redeemed), 0),
  ),
)

/** y 軸刻度：0／半／滿。半數不是整數時（例如上界 5）就只畫頭尾，免得出現 2.5 這種計數。 */
const yTicks = computed(() => {
  const top = maxValue.value
  const mid = top / 2
  return Number.isInteger(mid) && mid > 0 ? [0, mid, top] : [0, top]
})

function xOf(i: number): number {
  const n = props.points.length
  if (n <= 1) return PAD.l + innerW.value / 2
  return PAD.l + (i / (n - 1)) * innerW.value
}
function yOf(v: number): number {
  const top = maxValue.value
  return PAD.t + (1 - Math.max(0, Math.min(top, v)) / top) * innerH.value
}

/** 兩條線的 polyline points 字串（單點時另外畫圓點——polyline 畫不出單點）。 */
const paths = computed(() =>
  SERIES.map((s) => ({
    ...s,
    line: props.points.map((p, i) => `${xOf(i)},${yOf(p[s.key])}`).join(' '),
    dot:
      props.points.length === 1
        ? { x: xOf(0), y: yOf(props.points[0][s.key]) }
        : null,
  })),
)

const xTicks = computed(() => {
  const pts = props.points
  if (!pts.length) return []
  const n = Math.min(6, pts.length)
  const anchor = (i: number) => (i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle')
  const out: { x: number; label: string; anchor: string }[] = []
  for (let i = 0; i < n; i++) {
    const idx = n === 1 ? 0 : Math.round((i * (pts.length - 1)) / (n - 1))
    out.push({ x: xOf(idx), label: pts[idx].date.slice(5), anchor: anchor(i) })
  }
  return out
})

const totals = computed(() => ({
  issued: props.points.reduce((s, p) => s + p.issued, 0),
  redeemed: props.points.reduce((s, p) => s + p.redeemed, 0),
}))

// --- hover：找最近的一天 ---
const hoverIdx = ref<number | null>(null)
const hover = computed(() => {
  const i = hoverIdx.value
  if (i == null || i < 0 || i >= props.points.length) return null
  const p = props.points[i]
  return {
    x: xOf(i),
    date: p.date,
    rows: SERIES.map((s) => ({ label: s.label, cls: s.cls, v: p[s.key], y: yOf(p[s.key]) })),
  }
})

function onMove(e: MouseEvent) {
  const el = box.value
  if (!el || !props.points.length) return
  const x = e.clientX - el.getBoundingClientRect().left
  const n = props.points.length
  const i = n <= 1 ? 0 : Math.round(((x - PAD.l) / innerW.value) * (n - 1))
  hoverIdx.value = Math.max(0, Math.min(n - 1, i))
}
function onLeave() {
  hoverIdx.value = null
}

/** tooltip 靠左還是靠右（避免超出容器）。 */
const tipStyle = computed<Record<string, string>>(() => {
  const h = hover.value
  if (!h) return {}
  const flip = h.x > width.value - 140
  const style: Record<string, string> = {}
  if (flip) style.right = `${Math.max(4, width.value - h.x + 8)}px`
  else style.left = `${h.x + 8}px`
  return style
})
</script>

<template>
  <div ref="box" class="chart-wrap">
    <div class="legend">
      <span v-for="s in SERIES" :key="s.key" class="lg" :class="s.cls">
        <i />
        {{ s.label }} {{ totals[s.key] }}
      </span>
      <span class="spacer" />
      <span class="subtle">{{ points.length }} 天</span>
    </div>

    <div v-if="!points.length" class="empty subtle">這個期間沒有任何資料。</div>

    <div
      v-else
      class="plot"
      :style="{ height: `${height}px` }"
      @mousemove="onMove"
      @mouseleave="onLeave"
    >
      <svg :width="width" :height="height" role="img" aria-label="每日發碼與兌換趨勢">
        <g class="grid">
          <template v-for="t in yTicks" :key="t">
            <line :x1="PAD.l" :x2="width - PAD.r" :y1="yOf(t)" :y2="yOf(t)" />
            <text :x="PAD.l - 6" :y="yOf(t) + 3" text-anchor="end">{{ t }}</text>
          </template>
        </g>
        <g class="xax">
          <text v-for="(t, i) in xTicks" :key="i" :x="t.x" :y="height - 6" :text-anchor="t.anchor">
            {{ t.label }}
          </text>
        </g>
        <g class="series">
          <template v-for="s in paths" :key="s.key">
            <polyline v-if="!s.dot" :class="s.cls" :points="s.line" />
            <circle v-else :class="s.cls" :cx="s.dot.x" :cy="s.dot.y" r="2.5" />
          </template>
        </g>
        <g v-if="hover" class="hov">
          <line :x1="hover.x" :x2="hover.x" :y1="PAD.t" :y2="height - PAD.b" />
          <circle
            v-for="(r, i) in hover.rows"
            :key="i"
            :class="r.cls"
            :cx="hover.x"
            :cy="r.y"
            r="3.5"
          />
        </g>
      </svg>

      <div v-if="hover" class="tip" :style="tipStyle">
        <div class="tstamp">{{ hover.date }}</div>
        <div v-for="r in hover.rows" :key="r.label" class="trow" :class="r.cls">
          <i /><span class="tl">{{ r.label }}</span><span class="tv">{{ r.v }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.chart-wrap {
  position: relative;
  width: 100%;
  min-width: 0;
}
.legend {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  margin-bottom: var(--sp-2);
  font-size: var(--fs-11);
  color: var(--fg-3);
}
.legend .spacer {
  flex: 1;
}
.lg {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.lg i,
.trow i {
  display: inline-block;
  width: 9px;
  height: 3px;
  border-radius: 2px;
  background: currentColor;
}
.s-issued {
  color: var(--brand-500);
}
.s-redeemed {
  color: var(--success, #16a34a);
}
.empty {
  padding: var(--sp-5) 0;
  text-align: center;
  font-size: var(--fs-12);
}
.plot {
  position: relative;
}
svg {
  display: block;
}
.grid line {
  stroke: var(--border-2);
  stroke-width: 1;
}
.grid text,
.xax text {
  fill: var(--fg-4);
  font-size: 10px;
}
.series polyline {
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linejoin: round;
  stroke-linecap: round;
}
.series circle,
.hov circle {
  fill: currentColor;
  stroke: var(--bg-panel);
  stroke-width: 1;
}
.hov line {
  stroke: var(--border-strong);
  stroke-width: 1;
  stroke-dasharray: 3 3;
}
.tip {
  position: absolute;
  top: 4px;
  z-index: 5;
  min-width: 110px;
  padding: 6px 9px;
  background: var(--bg-elev);
  border: 1px solid var(--border);
  border-radius: var(--r-sm);
  box-shadow: var(--shadow-md);
  pointer-events: none;
  font-size: var(--fs-11);
}
.tstamp {
  color: var(--fg-3);
  margin-bottom: 3px;
  font-variant-numeric: tabular-nums;
}
.trow {
  display: flex;
  align-items: center;
  gap: 5px;
}
.trow .tl {
  flex: 1;
  color: var(--fg-2);
}
.trow .tv {
  color: var(--fg);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
</style>
